/**
 * usePhotoDownload Hook
 * 
 * Handles downloading photos to the device's camera roll.
 */

import { useState, useCallback } from "react";
import * as FileSystem from "expo-file-system/legacy";
import * as MediaLibrary from "expo-media-library";
import { Alert } from "react-native";
import type { Photo } from "../types";

interface DownloadProgress {
  photoId: string;
  progress: number;
  status: "pending" | "downloading" | "saving" | "complete" | "error";
  error?: string;
}

interface UsePhotoDownloadReturn {
  isDownloading: boolean;
  downloadProgress: DownloadProgress[];
  downloadPhoto: (photo: Photo) => Promise<boolean>;
  downloadMultiple: (photos: Photo[]) => Promise<number>;
  cancelDownload: () => void;
}

/**
 * Hook to handle photo downloads
 */
export const usePhotoDownload = (): UsePhotoDownloadReturn => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress[]>([]);
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  /**
   * Request media library permissions
   */
  const requestPermissions = async (): Promise<boolean> => {
    const { status } = await MediaLibrary.requestPermissionsAsync();
    if (status !== "granted") {
      Alert.alert(
        "Toestemming vereist",
        "Geef toegang tot je foto's om afbeeldingen op te slaan."
      );
      return false;
    }
    return true;
  };

  /**
   * Download a single photo
   */
  const downloadPhoto = useCallback(async (photo: Photo): Promise<boolean> => {
    if (!photo.file_url) return false;

    // Request permissions
    const hasPermission = await requestPermissions();
    if (!hasPermission) return false;

    try {
      setDownloadProgress(prev => [...prev, {
        photoId: photo.id,
        progress: 0,
        status: "downloading",
      }]);

      // Create download path
      const fileExtension = photo.file_type?.includes("png") ? "png" : "jpg";
      const fileName = `staatsen_${photo.id}.${fileExtension}`;
      const downloadPath = `${FileSystem.cacheDirectory}${fileName}`;

      // Download file
      const downloadResult = await FileSystem.downloadAsync(
        photo.file_url,
        downloadPath
      );

      if (downloadResult.status !== 200) {
        throw new Error("Download mislukt");
      }

      setDownloadProgress(prev => prev.map(p =>
        p.photoId === photo.id ? { ...p, progress: 80, status: "saving" } : p
      ));

      // Save to camera roll
      const asset = await MediaLibrary.createAssetAsync(downloadResult.uri);
      
      // Create album if it doesn't exist
      const album = await MediaLibrary.getAlbumAsync("Staatsen");
      if (album) {
        await MediaLibrary.addAssetsToAlbumAsync([asset], album, false);
      } else {
        await MediaLibrary.createAlbumAsync("Staatsen", asset, false);
      }

      setDownloadProgress(prev => prev.map(p =>
        p.photoId === photo.id ? { ...p, progress: 100, status: "complete" } : p
      ));

      return true;
    } catch (err) {
      console.error("Error downloading photo:", err);
      setDownloadProgress(prev => prev.map(p =>
        p.photoId === photo.id 
          ? { ...p, status: "error", error: "Download mislukt" } 
          : p
      ));
      return false;
    }
  }, []);

  /**
   * Download multiple photos
   */
  const downloadMultiple = useCallback(async (photos: Photo[]): Promise<number> => {
    if (photos.length === 0) return 0;

    // Request permissions
    const hasPermission = await requestPermissions();
    if (!hasPermission) return 0;

    setIsDownloading(true);
    setDownloadProgress([]);

    const controller = new AbortController();
    setAbortController(controller);

    let successCount = 0;

    for (const photo of photos) {
      if (controller.signal.aborted) break;

      const success = await downloadPhoto(photo);
      if (success) successCount++;
    }

    setIsDownloading(false);
    setAbortController(null);

    // Show result
    if (successCount > 0) {
      Alert.alert(
        "Download voltooid",
        `${successCount} van ${photos.length} foto's opgeslagen in je galerij.`
      );
    }

    return successCount;
  }, [downloadPhoto]);

  /**
   * Cancel ongoing download
   */
  const cancelDownload = useCallback(() => {
    abortController?.abort();
    setIsDownloading(false);
  }, [abortController]);

  return {
    isDownloading,
    downloadProgress,
    downloadPhoto,
    downloadMultiple,
    cancelDownload,
  };
};
